import React from 'react';

import createHash from '../../utility/create-hash';
import { StorageAdapterInterface } from './types';

const KEY_SEPARATOR = '::';
const VERSION_SUFFIX = '::version';

export type ContextProps = ReturnType<StorageProvider['getContextProps']>;

const { Provider, Consumer } = React.createContext<ContextProps>({
    // fallback when provider is missing
    cached: () => () => Promise.reject(Error('Render tree is not wrapped with CacheProvider')),
});

type Props = {
    version: string; // storageVersion
    storageKey: string;
    storageAdapter: StorageAdapterInterface;
    stateKey: string;
};

type State = {
    isLoading: boolean;
};

class StorageProvider extends React.Component<Props, State> {
    static defaultProps = {
        storageKey: 'cache',
        stateKey: 'redux',
    };

    state: State = {
        isLoading: true,
    };

    UNSAFE_componentWillMount() {
        this.clearInvalidatedCache().then(() => {
            this.setState({ isLoading: false });
        });
    }

    getContextProps = () => ({
        cached: this.cached,
    });

    getKeyPrefix = () => {
        const { storageKey, version } = this.props;
        return storageKey + version + KEY_SEPARATOR;
    };

    deriveKeyFromFunction = (func: (args: any[]) => Promise<any>, args: any[]) => {
        const functionName = func.name;
        const functionHash = createHash(func.toString());
        const argsString = JSON.stringify(args);

        // cache key -> 'functionName(1,2,3)_functionHash'
        return `${functionName}(${argsString})_${functionHash}`;
    };

    clearInvalidatedCache = (): Promise<void> => {
        const { storageAdapter, stateKey, storageKey, version } = this.props;

        return storageAdapter
            .get<string | undefined>(storageKey + VERSION_SUFFIX)
            .then((lastVersion) => {
                if (lastVersion === version) {
                    return;
                }

                // version invalidated, bump version & clear stored state
                storageAdapter.get(stateKey).then((state) => {
                    if (state && state?.session) {
                        const { session, organization } = state;
                        storageAdapter.set(stateKey, {
                            session,
                            organization,
                        });
                    } else {
                        storageAdapter.clear();
                    }

                    storageAdapter.set(storageKey + VERSION_SUFFIX, version);
                });
            });
    };

    cached =
        (func: (...args: any[]) => Promise<any>) =>
        (...args: any[]): Promise<any> => {
            const { storageAdapter } = this.props;

            if (storageAdapter == null) {
                throw Error('Cache storage is missing.');
            }

            const cachedResourceKey = this.getKeyPrefix() + this.deriveKeyFromFunction(func, args);

            return storageAdapter.hasKey(cachedResourceKey).then((hasKey) => {
                if (hasKey) {
                    return storageAdapter.get(cachedResourceKey);
                }

                return func(...args).then((result) =>
                    storageAdapter.set(cachedResourceKey, result).then(() => result)
                );
            });
        };

    render() {
        if (this.state.isLoading) {
            // @todo Display something here
            return null;
        }

        return <Provider value={this.getContextProps()}>{this.props.children}</Provider>;
    }
}

export { StorageProvider, Consumer as StorageConsumer };
